/**
 * EpisodeTrail — Alvo C round2 (mockup 02/2a): a mini-timeline de episódios no
 * cabeçalho do grupo por pessoa.
 *
 * Língua de cor ÚNICA do produto (decisão da rodada 2): verde = resolvida ·
 * cinza = arquivada/encerrada sem desfecho positivo · vermelho = ativa.
 * Comprime aos ~8 pontos mais recentes + «···» quando há mais. Ponto clicável
 * abre a conversa daquele episódio; hover mostra data + desfecho (title).
 */
import {
  conversationOutcomeLabel,
  conversationStatusLabel,
  formatShortDate,
  TERMINAL_CONVERSATION_STATUSES,
} from '../persons/persons-adapter';

import type { PersonConversation } from '../persons/types';

export const TRAIL_MAX_DOTS = 8;

export type EpisodeDotKind = 'active' | 'done' | 'muted';

/**
 * Função pura — testável sem render (padrão inbox-defaults).
 *
 * O wire entrega o status CRU do Prisma (snake_case) — a classificação deriva
 * da MESMA fonte que o resto da ficha usa (`TERMINAL_CONVERSATION_STATUSES`),
 * nunca de uma segunda lista à mão (F.5 C1: a lista paralela nasceu em
 * kebab-case e matou o canal vermelho da trilha inteira).
 * Vivo = não-terminal; verde = desfecho resolvido (por outcome OU status).
 */
export function episodeDotKind(episode: {
  status: string;
  outcomeType?: string | null;
}): EpisodeDotKind {
  if (!TERMINAL_CONVERSATION_STATUSES.has(episode.status)) return 'active';
  if (episode.outcomeType === 'resolved' || episode.status === 'resolved') return 'done';
  return 'muted';
}

function dotTitle(e: PersonConversation): string {
  const date = formatShortDate(e.updatedAt);
  const outcome =
    e.outcomeType !== undefined && e.outcomeType !== null
      ? conversationOutcomeLabel(e.outcomeType)
      : conversationStatusLabel(e.status);
  return `${date} · ${outcome}`;
}

export function EpisodeTrail({
  episodes,
  totalCount,
  onOpen,
}: {
  /** Episódios na ordem do endpoint (mais recente primeiro). */
  episodes: PersonConversation[];
  /** Total REAL da pessoa (meta.totalCount) — dita o «···» de compressão. */
  totalCount: number;
  /** Abrir a conversa do episódio clicado. */
  onOpen?: (conversationId: string) => void;
}): JSX.Element | null {
  if (episodes.length === 0) return null;
  // Cronológica esquerda→direita: mais antigo à esquerda, corrente à direita.
  const visible = episodes.slice(0, TRAIL_MAX_DOTS).reverse();
  const compressed = totalCount > visible.length;

  return (
    <span className="episode-trail" data-testid="episode-trail">
      {compressed && (
        <span className="episode-trail__more" aria-hidden>
          ···
        </span>
      )}
      {visible.map((e) => (
        <button
          key={e.id}
          type="button"
          className={`episode-trail__dot episode-trail__dot--${episodeDotKind(e)}`}
          title={dotTitle(e)}
          aria-label={`Episódio ${dotTitle(e)}`}
          onClick={(ev) => {
            ev.stopPropagation();
            onOpen?.(e.id);
          }}
        />
      ))}
    </span>
  );
}
